import { calculateComponentArea } from 'features/fde'
import { useMemo } from 'react'
import {
  GeckoComponentModel,
  GeckoMarkupLayer,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'

/**
 * Hook to calculate the ratio of area covered by scans versus the total area of the component
 *
 * @returns 0-1 ratio of area coverage.
 */
export const useScanAreaRatio = (
  markupLayersByInspection: ReadonlyMap<string, readonly GeckoMarkupLayer[]>,
  componentModelsByInspection: ReadonlyMap<string, GeckoComponentModel>
): number =>
  useMemo(
    () =>
      computeScanAreaRatio(
        markupLayersByInspection,
        componentModelsByInspection
      ),
    [componentModelsByInspection, markupLayersByInspection]
  )

/**
 * Hook to calculate the total area covered by scans
 *
 * @returns The total area covered by scans as a GeckoMeasurement
 */
export const useScanArea = (
  markupLayersByInspection: ReadonlyMap<string, readonly GeckoMarkupLayer[]>,
  componentModelsByInspection: ReadonlyMap<string, GeckoComponentModel>
): GeckoMeasurement =>
  useMemo(
    () =>
      computeScanArea(markupLayersByInspection, componentModelsByInspection),
    [componentModelsByInspection, markupLayersByInspection]
  )

/**
 * Utility function to calculate the ratio of area covered by scans versus the total area of the component
 *
 * @param markupLayersByInspection - The markup layers by inspection to calculate the scan area ratio from
 * @param componentModelsByInspection - The component models by inspection to calculate the scan area ratio from
 * @returns 0-1 ratio of area coverage.
 */
export const computeScanAreaRatio = (
  markupLayersByInspection: ReadonlyMap<string, readonly GeckoMarkupLayer[]>,
  componentModelsByInspection: ReadonlyMap<string, GeckoComponentModel>
): number => {
  const totalScanArea = computeScanArea(
    markupLayersByInspection,
    componentModelsByInspection
  )

  const totalComponentArea = computeTotalComponentArea(
    componentModelsByInspection
  )

  return totalComponentArea.num > 0
    ? totalScanArea.num / totalComponentArea.num
    : 0
}

/**
 * Utility function to calculate the area covered by scans across multiple inspections
 * All calculations done in square meters for consistency
 *
 * @param markupLayersByInspection - The markup layers by inspection to calculate the scan area from
 * @param componentModelsByInspection - The component models by inspection to calculate the scan area from
 * @returns The total area covered by scans as a GeckoMeasurement in square meters
 */
export const computeScanArea = (
  markupLayersByInspection: ReadonlyMap<string, readonly GeckoMarkupLayer[]>,
  componentModelsByInspection: ReadonlyMap<string, GeckoComponentModel>
): GeckoMeasurement => {
  let totalScanArea = 0

  markupLayersByInspection.forEach((markupLayers, inspectionId) => {
    const selectedComponentModel = componentModelsByInspection.get(inspectionId)

    const scanOverlaysLayer = markupLayers.find(
      (layer) =>
        layer.data?.name === 'Scan Overlay' &&
        Array.isArray(layer.data?.path) &&
        layer.data?.path[0] === 'Report'
    )

    if (!(scanOverlaysLayer && selectedComponentModel)) return

    const scanArea = scanOverlaysLayer.data.rectPrimitives
      .map((rect) => {
        // Convert to meters for consistent unit calculation
        const widthMeters = rect.bounds.x.size.toNumber(
          GeckoMeasurementUnit.Meters
        )
        const heightMeters = rect.bounds.y.size.toNumber(
          GeckoMeasurementUnit.Meters
        )
        return widthMeters * heightMeters
      })
      .reduce((prevNum, currNum) => prevNum + currNum, 0)

    totalScanArea += scanArea
  })

  return GeckoMeasurement.CreateFromValue(
    totalScanArea,
    GeckoMeasurementUnit.SquareMeters
  )
}

/**
 * Utility function to calculate the total area of all component models in square meters
 *
 * @returns The total area of all component models in square meters as a GeckoMeasurement
 */
export const computeTotalComponentArea = (
  componentModelsByInspection: ReadonlyMap<string, GeckoComponentModel>
): GeckoMeasurement => {
  const totalArea = componentModelsByInspection
    .values()
    .reduce((acc: number, componentModel: GeckoComponentModel) => {
      const componentAreaAsMeasurement: GeckoMeasurement =
        calculateComponentArea(componentModel)
      const componentArea = componentAreaAsMeasurement.num // in square meters

      if (!isNaN(componentArea)) {
        return acc + componentArea
      }

      return acc
    }, 0)

  return GeckoMeasurement.CreateFromValue(
    totalArea,
    GeckoMeasurementUnit.SquareMeters
  )
}
